<template>
    <v-container class="mb-12">
        <v-row justify="center">
            <v-col cols="12" md="8" lg="6">
                <v-card>
                    <v-card-title class="primary secondary--text d-block text-center">RETIRAR DE WALLET</v-card-title>
                    <v-card-text>
                        <v-form ref="form" v-model="valid" @submit.prevent="submit">
                            <v-select
                                v-model="form.source_currency"
                                :items="walletCurrencies"
                                item-text="code"
                                item-value="code"
                                label="Saldo a usar"
                                prepend-icon="mdi-wallet"
                                :rules="[rules.required]"
                                @change="refreshAmountValidation"/>
                            <v-select
                                v-model="form.currency"
                                :items="currencies"
                                item-text="code"
                                item-value="code"
                                label="Moneda destino"
                                :rules="[rules.required]"/>
                            <v-currency-field
                                ref="amountField"
                                v-model="form.amount"
                                label="Monto a retirar"
                                :suffix="form.source_currency"
                                prepend-icon="mdi-cash-minus"
                                v-bind="currencyFieldConfig(form.source_currency)"
                                :rules="amountRules"
                                @input="refreshAmountValidation"/>
                            <v-alert dense text type="info" v-if="amountLimitText">
                                {{ amountLimitText }}
                            </v-alert>
                            <v-textarea
                                v-model="form.note"
                                label="Datos o nota para el retiro"
                                rows="3"/>
                        </v-form>
                    </v-card-text>
                    <v-card-actions>
                        <v-btn text to="/"><v-icon left>mdi-arrow-left</v-icon> Volver</v-btn>
                        <v-spacer/>
                        <v-btn color="primary" :loading="loading" :disabled="loading || !valid" @click="submit">
                            <v-icon left>mdi-bank-transfer-out</v-icon> Solicitar
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import axios from 'axios';
import {
    currencyFieldConfig as moneyCurrencyFieldConfig,
    parseMoney
} from '../../helpers/money';

export default {
    name: "WalletWithdraw",
    data: () => ({
        valid: true,
        loading: false,
        currencies: [],
        walletCurrencies: [],
        allCurrencies: [],
        limits: {
            withdrawal: {
                minimums: {},
                maximums: {}
            }
        },
        form: {
            currency: 'USD',
            source_currency: 'USDC',
            amount: null,
            note: ''
        },
        rules: {
            required: v => !!v || 'Campo obligatorio.'
        }
    }),
    created() {
        this.getWallet();
    },
    computed: {
        amountRules() {
            return [this.rules.required, this.amountLimitRule];
        },
        amountLimitText() {
            const minimum = this.amountLimit('min');
            const maximum = this.amountLimit('max');

            if (minimum && maximum) {
                return 'Monto permitido: ' + minimum.formatted + ' a ' + maximum.formatted + '.';
            }

            if (minimum) {
                return 'Monto minimo: ' + minimum.formatted + '.';
            }

            if (maximum) {
                return 'Monto maximo: ' + maximum.formatted + '.';
            }

            return '';
        }
    },
    methods: {
        getWallet() {
            axios.get('/api/wallet').then(res => {
                const withdrawalOptions = res.data.quote_options && res.data.quote_options.withdrawal
                    ? res.data.quote_options.withdrawal
                    : [];
                const walletOptions = res.data.quote_options && res.data.quote_options.withdrawal_wallet_currencies
                    ? res.data.quote_options.withdrawal_wallet_currencies
                    : [];

                this.allCurrencies = res.data.currencies || [];
                this.walletCurrencies = walletOptions.length ? walletOptions : (res.data.wallet_currencies || []);
                this.currencies = withdrawalOptions.length ? withdrawalOptions : this.fiatCurrencyItems(this.allCurrencies);
                this.limits = res.data.limits || this.limits;

                if (this.walletCurrencies.length && !this.walletCurrencies.some(item => item.code === this.form.source_currency)) {
                    this.form.source_currency = this.walletCurrencies[0].code;
                }
                if (this.currencies.length && !this.currencies.some(item => item.code === this.form.currency)) {
                    this.form.currency = this.currencies[0].code;
                }
            });
        },
        fiatCurrencyItems(items) {
            const walletCodes = (this.walletCurrencies || []).map(item => String(item.code || '').toUpperCase());

            return (items || []).filter(item => {
                const code = String((typeof item === 'string' ? item : item.code) || '').toUpperCase();

                return walletCodes.indexOf(code) === -1;
            });
        },
        currencySources() {
            return []
                .concat(this.currencies || [])
                .concat(this.walletCurrencies || [])
                .concat(this.allCurrencies || []);
        },
        currencyFieldConfig(code) {
            return moneyCurrencyFieldConfig(this.currencySources(), code);
        },
        amountValue(value) {
            return parseMoney(value, this.currencySources(), this.form.source_currency);
        },
        amountLimit(kind) {
            const group = this.limits && this.limits.withdrawal ? this.limits.withdrawal : {};
            const key = kind === 'min' ? 'minimums' : 'maximums';
            const map = group[key] || {};

            return map[this.form.source_currency] || null;
        },
        amountLimitError(value) {
            const amount = this.amountValue(value !== undefined ? value : this.form.amount);
            const minimum = this.amountLimit('min');
            const maximum = this.amountLimit('max');

            if (amount <= 0) {
                return '';
            }

            if (minimum && amount + 0.0001 < Number(minimum.amount)) {
                return 'El monto minimo es ' + minimum.formatted + '.';
            }

            if (maximum && amount - 0.0001 > Number(maximum.amount)) {
                return 'El monto maximo es ' + maximum.formatted + '.';
            }

            return '';
        },
        amountLimitRule(value) {
            return this.amountLimitError(value) || true;
        },
        refreshAmountValidation() {
            this.$nextTick(() => {
                const field = this.$refs.amountField;
                if (!field || typeof field.resetValidation !== 'function' || typeof field.validate !== 'function') {
                    return;
                }

                if (this.amountValue(this.form.amount) <= 0) {
                    field.resetValidation();
                    return;
                }

                field.validate(true);
            });
        },
        submit() {
            if (!this.$refs.form.validate()) return;
            this.loading = true;
            axios.post('/api/wallet/withdrawal', Object.assign({}, this.form, {
                source_currency: this.form.source_currency,
                amount: this.amountValue(this.form.amount)
            })).then(res => {
                this.$toast.success(res.data.status, {icon: 'mdi-check-circle'});
                this.$router.push('/');
            }).catch(err => {
                const msg = err.response && err.response.data && err.response.data.status ? err.response.data.status : 'No fue posible solicitar el retiro.';
                this.$toast.error(msg, {icon: 'mdi-alert-circle'});
            }).then(() => {
                this.loading = false;
            });
        }
    }
}
</script>
